import { chatReadStateService } from '@/entities/ChatDialog/api/chatReadStateService';
import { getAppChatApi } from '@/core/api/generatedApiClient';
import { rethrowHandledApiError } from '@/core/api/rethrowHandledApiError';
import type { AppChatService } from '../model/types';

/** Предоставляет чтение переписки владельца и действия поддержки */
export const createAppChatService = (ownerAppUserId?: string): AppChatService => ({
    /** Получает доступ к обновлениям владельца */
    async getRealtime() {
        try {
            return (await getAppChatApi().getAppChatRealtime({ ownerAppUserId })).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
    /** Получает согласованное состояние переписки */
    async getSnapshot(request) {
        try {
            return (await getAppChatApi()
                .getAppChatSnapshot({
                    ...request,
                    ownerAppUserId
                })).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
    /** Получает страницу переписок */
    async getDialogs(request) {
        try {
            return (await getAppChatApi()
                .getAppChats({
                    ...request,
                    ownerAppUserId
                })).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
    /** Получает страницу сообщений */
    async getMessages(request) {
        try {
            return (await getAppChatApi()
                .getAppChatMessages({
                    ...request,
                    ownerAppUserId
                })).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
    /** Получает счётчики владельца */
    async getCounters() {
        try {
            return (await getAppChatApi().getAppChatCounters({ ownerAppUserId })).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
    /** Повтор внешней отправки недоступен в переписке приложения */
    async retryMessage() {
        throw new Error('Повтор внешней отправки недоступен в переписке приложения');
    },
    /** Отвечает от имени поддержки */
    async sendMessage(request) {
        try {
            return (await getAppChatApi().sendAppChatMessage(request)).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
    /** Подтверждает прочтение поддержки */
    async markRead(dialogId, throughMessageId) {
        await chatReadStateService.confirm(true, dialogId, throughMessageId);
    },
    /** Находит поддержку без создания переписки */
    async findByCompanion(companionAppUserId) {
        try {
            return (await getAppChatApi().findAppChat({ companionAppUserId })).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
    /** Разрешает прежнюю или текущую ссылку на переписку */
    async resolve(chatId) {
        try {
            return (await getAppChatApi()
                .resolveAppChat({
                    chatId,
                    ownerAppUserId
                })).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
    /** Открывает переписку поддержки с пользователем */
    async getOrCreate(companionAppUserId) {
        try {
            return (await getAppChatApi()
                .getOrCreateAppChat({
                    createAppChatRequest: { companionAppUserId },
                })).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
    /** Получает активность переписки пользователей */
    async getActivity() {
        try {
            return (await getAppChatApi().getAppChatActivity()).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
});

/** Операции переписки поддержки в приложении */
export const appChatService = createAppChatService();
